import { useState, useEffect, useCallback, useMemo } from "react";
import { notification } from "antd";
import { useAppContext } from "../context/AppContext";
import { colorForUser } from "../utils/annotationShapes";
import * as annotationsApi from "../api/annotations";

const useAnnotations = (initialAnnotations = {}, initialKeys = [], caseId = null) => {
  const { userRole, professorName } = useAppContext();

  const [annotations, setAnnotations] = useState(initialAnnotations);
  const [pendingAnnotationData, setPendingAnnotationData] = useState(null);
  const [newAnnotationName, setNewAnnotationName] = useState("");
  const [newAnnotationDesc, setNewAnnotationDesc] = useState("");
  const [newAnnotationColor, setNewAnnotationColor] = useState(null);
  const [pendingModalOpen, setPendingModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [targetAnnotationIds, setTargetAnnotationIds] = useState({});
  const [sectionVisibility, setSectionVisibility] = useState(() =>
    initialKeys.reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {})
  );
  const [annotationVisibilityById, setAnnotationVisibilityById] = useState(() => {
    const initial = {};
    Object.values(initialAnnotations).forEach((list) =>
      (list || []).forEach((a) => {
        initial[a.id] = true;
      })
    );
    return initial;
  });

  // Reset target annotation IDs after a short delay (one-shot signal)
  useEffect(() => {
    const handler = setTimeout(() => {
      const keysWithTargets = Object.keys(targetAnnotationIds).filter(
        (k) => targetAnnotationIds[k] !== null
      );
      if (keysWithTargets.length > 0) {
        setTargetAnnotationIds((prev) => {
          const next = { ...prev };
          keysWithTargets.forEach((k) => (next[k] = null));
          return next;
        });
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [targetAnnotationIds]);

  // Visible annotations filtered by author privacy rules.
  // Prof sees only Prof annotations; Etudiant sees Prof + own (in this single-user demo, all Etudiant).
  const visibleAnnotationsByKey = useMemo(() => {
    const out = {};
    Object.entries(annotations).forEach(([key, list]) => {
      out[key] = (list || []).filter((a) => {
        if (userRole === "Professeur") return a.authorRole === "Professeur";
        return a.authorRole === "Professeur" || a.authorRole === "Etudiant";
      });
    });
    return out;
  }, [annotations, userRole]);

  // What to actually render in viewers: filter by section + per-annotation
  // visibility. Per-annotation visibility is the single source of truth — the
  // author toggle drives it (see handleSetAuthorVisibility), so hiding an author
  // and hiding their individual cards stay in lockstep.
  const renderableAnnotationsByKey = useMemo(() => {
    const out = {};
    Object.entries(visibleAnnotationsByKey).forEach(([key, list]) => {
      const sectionOn = sectionVisibility[key] !== false;
      if (!sectionOn) {
        out[key] = [];
        return;
      }
      out[key] = (list || []).filter(
        (a) => annotationVisibilityById[a.id] !== false
      );
    });
    return out;
  }, [visibleAnnotationsByKey, sectionVisibility, annotationVisibilityById]);

  // Authors grouped by role, with annotation counts — drives the "by author"
  // section at the top of the annotations panel.
  const authorsByRole = useMemo(() => {
    const groups = { Professeur: {}, Etudiant: {} };
    Object.values(visibleAnnotationsByKey).forEach((list) =>
      (list || []).forEach((a) => {
        const role = a.authorRole === "Professeur" ? "Professeur" : "Etudiant";
        const name = a.authorName || "—";
        groups[role][name] = (groups[role][name] || 0) + 1;
      })
    );
    return groups;
  }, [visibleAnnotationsByKey]);

  // Author eye-state is derived from its annotations: an author shows as
  // visible when at least one of their annotations is currently visible. This
  // keeps the author toggle in sync after individual cards are toggled.
  const authorVisibility = useMemo(() => {
    const out = {};
    Object.values(visibleAnnotationsByKey).forEach((list) =>
      (list || []).forEach((a) => {
        const name = a.authorName || "—";
        out[name] = out[name] || annotationVisibilityById[a.id] !== false;
      })
    );
    return out;
  }, [visibleAnnotationsByKey, annotationVisibilityById]);

  // Toggling an author propagates to every one of their annotations, so the
  // per-annotation cards in the panel (and the canvas) reflect it immediately.
  const handleSetAuthorVisibility = useCallback(
    (authorName, visible) => {
      setAnnotationVisibilityById((prev) => {
        const next = { ...prev };
        Object.values(annotations).forEach((list) =>
          (list || []).forEach((a) => {
            if ((a.authorName || "—") === authorName) next[a.id] = visible;
          })
        );
        return next;
      });
    },
    [annotations]
  );

  const handleNewAnnotationData = useCallback(
    (annotationKey, annotationData, assetId = null) => {
      if (!annotationKey) {
        console.error("useAnnotations: missing annotationKey for new data");
        return;
      }
      const dataCopy = JSON.parse(JSON.stringify(annotationData));
      setPendingAnnotationData({ annotationKey, assetId, data: dataCopy });
      setNewAnnotationName("");
      setNewAnnotationDesc("");
      // Default to this author's per-user color; the picker can override it.
      setNewAnnotationColor(colorForUser(professorName));
      setPendingModalOpen(true);
    },
    [professorName]
  );

  const handleSaveAnnotation = useCallback(() => {
    if (!pendingAnnotationData || !newAnnotationName) return;

    const { annotationKey, assetId, data } = pendingAnnotationData;
    const uniqueId = `anno-${annotationKey}-${Date.now()}`;
    // Radiology (Cornerstone) annotations carry world-space `points` + a slice.
    // OSD/SVG viewers (macro, histo) carry a normalized `geometry` (+bounds for
    // rectangles). Branch on the modality, not on the presence of `bounds`, so
    // every histo/macro shape (ellipse, circle, arrow, freehand) persists.
    const isRadio = annotationKey.startsWith("radio:");

    let newAnnotation;
    if (!isRadio) {
      newAnnotation = {
        id: uniqueId,
        name: newAnnotationName,
        description: newAnnotationDesc,
        toolName: data.toolName,
        bounds: data.bounds,
        geometry: data.geometry,
        color: newAnnotationColor || colorForUser(professorName),
        assetId,
        authorRole: userRole,
        authorName: professorName,
        createdAt: new Date().toISOString(),
      };
    } else {
      const sliceIndex =
        typeof data.sliceIndex === "number"
          ? data.sliceIndex
          : parseInt(data.sliceIndex, 10);

      let points = [];
      const sourcePoints = data.handles?.points || data.points;
      if (!sourcePoints || sourcePoints.length < 2) {
        console.warn("Invalid points data in annotation", data);
        return;
      }

      if (data.toolName === "RectangleROI") {
        if (sourcePoints.length >= 4) {
          points = [
            [sourcePoints[0][0], sourcePoints[0][1], sliceIndex],
            [sourcePoints[2][0], sourcePoints[2][1], sliceIndex],
          ];
        } else {
          points = [
            [sourcePoints[0][0], sourcePoints[0][1], sliceIndex],
            [sourcePoints[1][0], sourcePoints[1][1], sliceIndex],
          ];
        }
      } else {
        points = sourcePoints.map((p) => [...p, sliceIndex]);
      }

      newAnnotation = {
        id: uniqueId,
        name: newAnnotationName,
        description: newAnnotationDesc,
        toolName: data.toolName,
        sliceIndex,
        points,
        frameOfReferenceUID: data.frameOfReferenceUID,
        color: newAnnotationColor || colorForUser(professorName),
        assetId,
        authorRole: userRole,
        authorName: professorName,
        createdAt: new Date().toISOString(),
      };
    }

    // Optimistic insert with a temporary id; reconcile with the server id below.
    setAnnotations((prev) => ({
      ...prev,
      [annotationKey]: [...(prev[annotationKey] || []), newAnnotation],
    }));
    setAnnotationVisibilityById((prev) => ({ ...prev, [uniqueId]: true }));
    setTargetAnnotationIds((prev) => ({ ...prev, [annotationKey]: uniqueId }));

    setPendingAnnotationData(null);
    setNewAnnotationName("");
    setNewAnnotationDesc("");
    setPendingModalOpen(false);

    // Persist to the backend, then swap the temp id for the server id.
    if (caseId) {
      annotationsApi
        .createAnnotation(caseId, annotationKey, newAnnotation)
        .then((saved) => {
          setAnnotations((prev) => ({
            ...prev,
            [annotationKey]: (prev[annotationKey] || []).map((a) =>
              a.id === uniqueId ? { ...a, ...saved } : a
            ),
          }));
          setAnnotationVisibilityById((prev) => {
            const next = { ...prev, [saved.id]: prev[uniqueId] !== false };
            delete next[uniqueId];
            return next;
          });
        })
        .catch((e) => {
          // Roll back the optimistic insert on failure.
          setAnnotations((prev) => ({
            ...prev,
            [annotationKey]: (prev[annotationKey] || []).filter(
              (a) => a.id !== uniqueId
            ),
          }));
          notification.error({
            message: "Annotation non enregistrée",
            description: e.message,
          });
        });
    }

    if (!isRadio) {
      const modality = annotationKey;
      if (modality === "histo") {
        document.dispatchEvent(
          new CustomEvent("histology-annotation-saved", {
            detail: { annotationId: uniqueId },
          })
        );
      } else if (modality === "macro") {
        document.dispatchEvent(
          new CustomEvent("macro-annotation-saved", {
            detail: { annotationId: uniqueId },
          })
        );
      }
    }
  }, [pendingAnnotationData, newAnnotationName, newAnnotationDesc, newAnnotationColor, userRole, professorName, caseId]);

  const handleCancelPending = useCallback(() => {
    const annotationKey = pendingAnnotationData?.annotationKey;
    if (annotationKey && !annotationKey.startsWith("radio:")) {
      if (annotationKey === "histo") {
        document.dispatchEvent(new CustomEvent("histology-annotation-canceled"));
      } else if (annotationKey === "macro") {
        document.dispatchEvent(new CustomEvent("macro-annotation-canceled"));
      }
    }
    setPendingAnnotationData(null);
    setNewAnnotationName("");
    setNewAnnotationDesc("");
    setPendingModalOpen(false);
  }, [pendingAnnotationData]);

  const handleDeleteAnnotation = useCallback(
    (annotationKey, annotationId) => {
      // Snapshot for rollback, then optimistically remove.
      let removed = null;
      setAnnotations((prev) => {
        removed = (prev[annotationKey] || []).find((a) => a.id === annotationId);
        return {
          ...prev,
          [annotationKey]:
            prev[annotationKey]?.filter((a) => a.id !== annotationId) || [],
        };
      });
      setAnnotationVisibilityById((prev) => {
        const next = { ...prev };
        delete next[annotationId];
        return next;
      });
      if (!caseId) return;
      annotationsApi.deleteAnnotation(annotationId).catch((e) => {
        if (removed) {
          setAnnotations((prev) => ({
            ...prev,
            [annotationKey]: [...(prev[annotationKey] || []), removed],
          }));
        }
        notification.error({
          message: "Suppression impossible",
          description: e.message,
        });
      });
    },
    [caseId]
  );

  // Recolor an existing annotation from the panel. Optimistic, persisted via
  // PUT (color rides inside the geometry JSON).
  const handleSetAnnotationColor = useCallback(
    (annotationKey, annotationId, color) => {
      const current = (annotations[annotationKey] || []).find(
        (a) => a.id === annotationId
      );
      if (!current) return;
      const original = current.color;
      const updated = { ...current, color };
      setAnnotations((prev) => ({
        ...prev,
        [annotationKey]: (prev[annotationKey] || []).map((a) =>
          a.id === annotationId ? updated : a
        ),
      }));
      if (!caseId) return;
      annotationsApi
        .updateAnnotation(annotationId, current.modalityKey || annotationKey, updated)
        .catch((e) => {
          setAnnotations((prev) => ({
            ...prev,
            [annotationKey]: (prev[annotationKey] || []).map((a) =>
              a.id === annotationId ? { ...a, color: original } : a
            ),
          }));
          notification.error({
            message: "Couleur non enregistrée",
            description: e.message,
          });
        });
    },
    [annotations, caseId]
  );

  const handleGoToAnnotation = useCallback((annotationKey, annotationId) => {
    setTargetAnnotationIds((prev) => ({ ...prev, [annotationKey]: annotationId }));
    // Force section + anno to be visible so the target is rendered
    setSectionVisibility((prev) => ({ ...prev, [annotationKey]: true }));
    setAnnotationVisibilityById((prev) => ({ ...prev, [annotationId]: true }));
  }, []);

  const handleSetSectionVisibility = useCallback((annotationKey, visible) => {
    setSectionVisibility((prev) => ({ ...prev, [annotationKey]: visible }));
  }, []);

  const handleSetAnnotationVisibility = useCallback((annotationId, visible) => {
    setAnnotationVisibilityById((prev) => ({ ...prev, [annotationId]: visible }));
  }, []);

  const handleShowAllGlobal = useCallback(() => {
    setSectionVisibility((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => (next[k] = true));
      return next;
    });
    setAnnotationVisibilityById((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((id) => (next[id] = true));
      return next;
    });
  }, []);

  const handleHideAllGlobal = useCallback(() => {
    setSectionVisibility((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => (next[k] = false));
      return next;
    });
  }, []);

  const initVisibilityForKey = useCallback((annotationKey) => {
    setSectionVisibility((prev) =>
      prev[annotationKey] === undefined
        ? { ...prev, [annotationKey]: true }
        : prev
    );
  }, []);

  return {
    // raw + filtered views
    annotations,
    visibleAnnotationsByKey,
    renderableAnnotationsByKey,

    // section visibility (per modality / series)
    sectionVisibility,
    handleSetSectionVisibility,

    // per-annotation visibility
    annotationVisibilityById,
    handleSetAnnotationVisibility,
    handleSetAnnotationColor,

    // per-author visibility (grouped by role)
    authorsByRole,
    authorVisibility,
    handleSetAuthorVisibility,

    // global toggles
    handleShowAllGlobal,
    handleHideAllGlobal,

    // pending creation flow
    pendingAnnotationData,
    pendingModalOpen,
    setPendingModalOpen,
    newAnnotationName,
    setNewAnnotationName,
    newAnnotationDesc,
    setNewAnnotationDesc,
    newAnnotationColor,
    setNewAnnotationColor,
    handleNewAnnotationData,
    handleSaveAnnotation,
    handleCancelPending,

    // deletion
    handleDeleteAnnotation,

    // go to (one-shot)
    targetAnnotationIds,
    handleGoToAnnotation,

    // drawer
    drawerOpen,
    setDrawerOpen,

    initVisibilityForKey,
  };
};

export default useAnnotations;
